import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";

import Navbar from "@/components/Navbar";
import MoonChatWidget from "@/components/MoonChatWidget";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";

import Home           from "@/pages/Home";
import Shop           from "@/pages/Shop";
import ProductDetail  from "@/pages/ProductDetail";
import Cart           from "@/pages/Cart";
import Checkout       from "@/pages/Checkout";
import About          from "@/pages/About";
import Account        from "@/pages/Account";
import StyleInspiration from "@/pages/StyleInspiration";
import ReturnPolicy from "@/pages/ReturnPolicy";
import Terms          from "@/pages/Terms";
import Privacy        from "@/pages/Privacy";
import Blog           from "@/pages/Blog";
import BlogPost       from "@/pages/BlogPost";
import AdminLogin     from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
};

const StoreLayout = ({ children }) => (
  <>
    <Navbar />
    {children}
    <Footer />
    <MoonChatWidget />
  </>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// AppRouter checks the URL hash synchronously (not in useEffect) so the
// Emergent OAuth session_id is processed BEFORE any protected route runs.
const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/"            element={<StoreLayout><Home /></StoreLayout>} />
        <Route path="/shop"        element={<StoreLayout><Shop /></StoreLayout>} />
        <Route path="/product/:id" element={<StoreLayout><ProductDetail /></StoreLayout>} />
        <Route path="/cart"        element={<StoreLayout><Cart /></StoreLayout>} />
        <Route path="/checkout"    element={<StoreLayout><Checkout /></StoreLayout>} />
        <Route path="/about"       element={<StoreLayout><About /></StoreLayout>} />
        <Route path="/account"     element={<StoreLayout><Account /></StoreLayout>} />
        <Route path="/style"       element={<StoreLayout><StyleInspiration /></StoreLayout>} />
        <Route path="/return-policy" element={<StoreLayout><ReturnPolicy /></StoreLayout>} />
        <Route path="/terms"         element={<StoreLayout><Terms /></StoreLayout>} />
        <Route path="/privacy"       element={<StoreLayout><Privacy /></StoreLayout>} />
        <Route path="/blog"          element={<StoreLayout><Blog /></StoreLayout>} />
        <Route path="/blog/:slug"    element={<StoreLayout><BlogPost /></StoreLayout>} />

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin"       element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <CustomerAuthProvider>
            <CartProvider>
              <BrowserRouter>
                <AppRouter />
                <Toaster richColors position="top-center" />
              </BrowserRouter>
            </CartProvider>
          </CustomerAuthProvider>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
